<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 flex items-center justify-center bg-slate-950/70 backdrop-blur-md p-3 sm:p-6 z-[10000] overflow-y-auto"
    @click.self="emit('close')"
  >
    <div
      class="bg-white dark:bg-[#111C30] rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-200/90 dark:border-[#263449] w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col transform transition-all duration-300 ease-out"
      :class="{
        'scale-100 opacity-100': isOpen,
        'scale-95 opacity-0': !isOpen,
      }"
    >
      <!-- Top Subtle Gradient Accent Line -->
      <div class="h-1 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 shrink-0"></div>

      <!-- Professional Modal Header Bar -->
      <div
        class="bg-white dark:bg-[#111C30] px-6 py-4 border-b border-slate-100 dark:border-[#263449] flex items-center justify-between shrink-0"
      >
        <div class="flex items-center gap-3">
          <div>
            <div class="flex items-center gap-2.5 flex-wrap">
              <h2 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight">
                Faculty Profile
              </h2>
              <span
                v-if="teacher?.attendanceID"
                class="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700/50"
              >
                ID: {{ teacher.attendanceID }}
              </span>
              <span
                class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold capitalize border"
                :class="
                  teacher?.status === 'active'
                    ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-500/30'
                    : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                "
              >
                <span
                  class="w-1.5 h-1.5 rounded-full"
                  :class="teacher?.status === 'active' ? 'bg-emerald-500' : 'bg-slate-400'"
                ></span>
                {{ teacher?.status || 'Active' }}
              </span>
            </div>
            <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-normal">
              {{ teacher?.designation || teacher?.role || 'Educator' }} • {{ teacher?.email || 'Faculty Member' }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button
            @click="emit('edit', teacher)"
            class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 bg-slate-50 dark:bg-[#18243A] hover:bg-slate-100 dark:hover:bg-[#1f2d47] border border-slate-200/80 dark:border-[#263449] rounded-xl transition-all"
          >
            <Pencil class="w-3.5 h-3.5" />
            <span>Edit Profile</span>
          </button>
          <button
            @click="emit('close')"
            class="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition-colors"
            title="Close"
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- Scrollable Modal Content -->
      <div
        v-if="teacher"
        class="overflow-y-auto p-5 sm:p-7 space-y-6 flex-1 bg-slate-50/50 dark:bg-[#0B1120]"
      >
        <!-- Hero Identity Card -->
        <div
          class="relative bg-white dark:bg-[#111C30] rounded-2xl p-5 sm:p-6 border border-slate-200/80 dark:border-[#263449] shadow-xs flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6"
        >
          <!-- Avatar with status indicator -->
          <div class="relative shrink-0">
            <div class="w-22 h-22 sm:w-26 sm:h-26 rounded-2xl overflow-hidden ring-4 ring-slate-100 dark:ring-slate-800 shadow-md">
              <img
                v-if="teacher.photo"
                :src="teacher.photo"
                :alt="teacher.name"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full bg-gradient-to-br from-indigo-500 via-indigo-600 to-violet-600 text-white flex items-center justify-center text-3xl font-bold tracking-wider"
              >
                {{ getInitials(teacher.name) }}
              </div>
            </div>
            <span
              class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full ring-2 ring-white dark:ring-[#111C30]"
              :class="
                teacher.status === 'active'
                  ? 'bg-emerald-500'
                  : 'bg-slate-400'
              "
              :title="teacher.status === 'active' ? 'Active Faculty' : 'Inactive'"
            ></span>
          </div>

          <!-- Hero Details -->
          <div class="flex-1 text-center sm:text-left min-w-0 w-full">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 class="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                  {{ teacher.name }}
                </h3>
                <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-2">
                  <!-- Role badge -->
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 capitalize border border-slate-200/70 dark:border-slate-700"
                  >
                    <Briefcase class="w-3.5 h-3.5 text-slate-400" />
                    <span>{{ teacher.role || 'Teacher' }}</span>
                  </span>

                  <!-- Subject badge -->
                  <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/60 shadow-2xs"
                  >
                    <BookOpen class="w-3.5 h-3.5 text-indigo-500" />
                    <span>{{ teacher.subject || 'General' }}</span>
                  </span>

                  <!-- Status capsule -->
                  <span
                    v-if="teacher.status === 'active'"
                    class="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/60 shadow-2xs"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    Active
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-900/60 shadow-2xs"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                    {{ teacher.status === 'passout' ? 'Inactive' : (teacher.status || 'Inactive') }}
                  </span>
                </div>
              </div>

              <!-- Leave Balance Button -->
              <div class="mt-2 sm:mt-0">
                <button
                  @click="showLeaveBalance = true"
                  class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-xs transition-colors duration-150"
                >
                  <Calendar class="w-4 h-4" />
                  <span>Leave Balance</span>
                </button>
              </div>
            </div>

            <!-- Monospace ID Bar -->
            <div class="mt-3.5 pt-3 border-t border-slate-100 dark:border-[#1E293B] flex items-center justify-center sm:justify-start gap-4 text-xs text-slate-500 dark:text-slate-400 font-mono">
              <span class="flex items-center gap-1.5">
                <Shield class="w-3.5 h-3.5 text-slate-400" />
                <span>Attendance ID:</span>
                <strong class="text-slate-700 dark:text-slate-200">{{ teacher.attendanceID || '—' }}</strong>
              </span>
            </div>
          </div>
        </div>

        <!-- 2-Column Info Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <!-- Card 1: Contact Information -->
          <div class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <User class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span>Contact Information</span>
            </div>
            <div class="space-y-3.5 text-xs">
              <!-- Email -->
              <div class="flex items-start gap-3">
                <Mail class="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Email Address</p>
                  <a
                    v-if="teacher.email"
                    :href="'mailto:' + teacher.email"
                    class="text-slate-800 dark:text-slate-200 font-medium hover:text-indigo-600 truncate block mt-0.5"
                  >
                    {{ teacher.email }}
                  </a>
                  <p v-else class="text-slate-400 mt-0.5">—</p>
                </div>
              </div>

              <!-- Phone -->
              <div class="flex items-start gap-3">
                <Phone class="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Phone Number</p>
                  <a
                    v-if="teacher.phone"
                    :href="'tel:' + teacher.phone"
                    class="text-slate-800 dark:text-slate-200 font-medium font-mono hover:text-indigo-600 block mt-0.5"
                  >
                    {{ teacher.phone }}
                  </a>
                  <p v-else class="text-slate-400 mt-0.5">—</p>
                </div>
              </div>

              <!-- Date of Birth -->
              <div class="flex items-start gap-3">
                <Cake class="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Date of Birth</p>
                  <div class="flex items-center gap-2 mt-0.5">
                    <span class="text-slate-800 dark:text-slate-200 font-medium">{{ formatDate(teacher.dob) }}</span>
                    <span
                      v-if="teacher?.dob"
                      class="text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/60 font-semibold text-[11px] px-2 py-0.5 rounded-md border border-indigo-100 dark:border-indigo-900/60"
                    >
                      {{ calculateAge(teacher.dob) }} yrs
                    </span>
                  </div>
                </div>
              </div>

              <!-- Status -->
              <div class="flex items-start gap-3">
                <Circle class="w-4 h-4 mt-0.5 shrink-0" :class="teacher.status === 'active' ? 'text-emerald-500' : 'text-rose-500'" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Current Status</p>
                  <span
                    :class="
                      teacher.status === 'active'
                        ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/60'
                        : 'bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-900/60'
                    "
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize mt-0.5"
                  >
                    {{ teacher.status === 'passout' ? 'Inactive' : (teacher.status || 'Inactive') }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Card 2: Professional & Identity -->
          <div class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <Briefcase class="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <span>Professional & Identity</span>
            </div>
            <div class="space-y-3.5 text-xs">
              <!-- Subject -->
              <div class="flex items-start gap-3">
                <BookOpen class="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Assigned Subject</p>
                  <span class="inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-200/80 dark:border-purple-900/60 mt-1">
                    {{ teacher.subject || 'General' }}
                  </span>
                </div>
              </div>

              <!-- Aadhaar Number -->
              <div class="flex items-start gap-3">
                <CreditCard class="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Aadhaar Number</p>
                  <span class="font-mono text-slate-800 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-md inline-block mt-1 font-medium">
                    {{ teacher.aadhaarNumber || 'Not provided' }}
                  </span>
                </div>
              </div>

              <!-- Aadhaar Document Attachment -->
              <div v-if="teacher.aadhaarCard" class="pt-2 border-t border-slate-100 dark:border-[#1E293B]">
                <div class="flex items-center gap-2 mb-2">
                  <FileText class="w-4 h-4 text-purple-500 shrink-0" />
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Aadhaar Document</p>
                </div>
                <div v-if="isImage(teacher.aadhaarCard)" class="mt-1">
                  <img
                    :src="teacher.aadhaarCard"
                    alt="Aadhaar Card"
                    class="max-w-[200px] h-auto rounded-xl border border-slate-200 dark:border-[#263449] shadow-xs hover:shadow-md transition-shadow"
                  />
                </div>
                <div v-else class="mt-1">
                  <a
                    :href="teacher.aadhaarCard"
                    target="_blank"
                    rel="noopener noreferrer"
                    :download="teacher.aadhaarCard.startsWith('data:') ? 'aadhaar_card.pdf' : null"
                    class="inline-flex items-center gap-2 text-purple-700 dark:text-purple-300 hover:text-purple-800 text-xs font-semibold bg-purple-50 dark:bg-purple-950/60 px-3 py-1.5 rounded-lg border border-purple-200 dark:border-purple-800 transition-colors"
                  >
                    <Paperclip class="w-3.5 h-3.5" />
                    <span>View / Download Document</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Card 3: Address & Location Details -->
        <div class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
          <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
            <MapPin class="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span>Address Details</span>
          </div>
          <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5 text-xs">
            <div class="bg-slate-50/80 dark:bg-[#0F172A] rounded-xl p-3 border border-slate-100 dark:border-[#1E293B]">
              <div class="flex items-center gap-1.5 text-slate-400 dark:text-slate-500 mb-1">
                <Home class="w-3.5 h-3.5 text-amber-500" />
                <span class="font-semibold uppercase text-[10px]">VTC</span>
              </div>
              <p class="font-medium text-slate-800 dark:text-slate-200 truncate">{{ teacher.vtc || '—' }}</p>
            </div>
            <div class="bg-slate-50/80 dark:bg-[#0F172A] rounded-xl p-3 border border-slate-100 dark:border-[#1E293B]">
              <div class="flex items-center gap-1.5 text-slate-400 dark:text-slate-500 mb-1">
                <Mailbox class="w-3.5 h-3.5 text-amber-500" />
                <span class="font-semibold uppercase text-[10px]">Post Office</span>
              </div>
              <p class="font-medium text-slate-800 dark:text-slate-200 truncate">{{ teacher.postOffice || '—' }}</p>
            </div>
            <div class="bg-slate-50/80 dark:bg-[#0F172A] rounded-xl p-3 border border-slate-100 dark:border-[#1E293B]">
              <div class="flex items-center gap-1.5 text-slate-400 dark:text-slate-500 mb-1">
                <Map class="w-3.5 h-3.5 text-amber-500" />
                <span class="font-semibold uppercase text-[10px]">District</span>
              </div>
              <p class="font-medium text-slate-800 dark:text-slate-200 truncate">{{ teacher.subDistrict || '—' }}</p>
            </div>
            <div class="bg-slate-50/80 dark:bg-[#0F172A] rounded-xl p-3 border border-slate-100 dark:border-[#1E293B]">
              <div class="flex items-center gap-1.5 text-slate-400 dark:text-slate-500 mb-1">
                <Building class="w-3.5 h-3.5 text-amber-500" />
                <span class="font-semibold uppercase text-[10px]">State</span>
              </div>
              <p class="font-medium text-slate-800 dark:text-slate-200 truncate">{{ teacher.state || '—' }}</p>
            </div>
            <div class="bg-slate-50/80 dark:bg-[#0F172A] rounded-xl p-3 border border-slate-100 dark:border-[#1E293B]">
              <div class="flex items-center gap-1.5 text-slate-400 dark:text-slate-500 mb-1">
                <Hash class="w-3.5 h-3.5 text-amber-500" />
                <span class="font-semibold uppercase text-[10px]">Pincode</span>
              </div>
              <p class="font-mono font-medium text-slate-800 dark:text-slate-200">{{ teacher.pincode || '—' }}</p>
            </div>
          </div>
        </div>

        <!-- Card 4: Qualifications -->
        <div
          v-if="teacher.qualifications && teacher.qualifications.length > 0"
          class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4"
        >
          <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
            <GraduationCap class="w-4 h-4 text-teal-600 dark:text-teal-400" />
            <span>Academic & Professional Qualifications</span>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              v-for="(q, index) in teacher.qualifications"
              :key="index"
              class="bg-slate-50/70 dark:bg-[#0F172A] border border-slate-200/70 dark:border-[#1E293B] rounded-xl p-4 space-y-2"
            >
              <div class="flex items-center justify-between gap-2">
                <span class="font-bold text-slate-900 dark:text-white text-sm">{{ q.type }}</span>
                <span class="font-bold text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-950/60 px-2.5 py-0.5 rounded-md border border-teal-200 dark:border-teal-800 text-xs">{{ q.year }}</span>
              </div>
              <p class="text-xs text-slate-600 dark:text-slate-400">
                Institution: <span class="font-semibold text-slate-800 dark:text-slate-200">{{ q.institution || '—' }}</span>
              </p>
              <div v-if="q.fileUrl" class="pt-2 border-t border-slate-200/60 dark:border-[#1E293B]">
                <a
                  v-if="isPdf(q.fileUrl)"
                  :href="q.fileUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  :download="q.fileUrl.startsWith('data:') ? `qualification_${index + 1}.pdf` : null"
                  class="inline-flex items-center gap-1.5 text-teal-700 dark:text-teal-300 hover:text-teal-800 text-xs font-semibold bg-teal-50/80 dark:bg-teal-950/60 px-2.5 py-1 rounded-md border border-teal-200 dark:border-teal-800 transition-colors"
                >
                  <Paperclip class="w-3.5 h-3.5" />
                  <span>View / Download Document</span>
                </a>
                <img
                  v-else-if="isImage(q.fileUrl)"
                  :src="q.fileUrl"
                  alt="Qualification Document"
                  class="max-w-[160px] h-auto rounded-lg border border-slate-200 dark:border-[#263449] mt-2"
                />
                <a
                  v-else
                  :href="q.fileUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1.5 text-teal-700 dark:text-teal-300 hover:text-teal-800 text-xs font-semibold bg-teal-50/80 dark:bg-teal-950/60 px-2.5 py-1 rounded-md border border-teal-200 dark:border-teal-800 transition-colors"
                >
                  <Paperclip class="w-3.5 h-3.5" />
                  <span>View Document</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Card 5: Remarks -->
        <div
          v-if="teacher.remark"
          class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-2"
        >
          <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-2 border-b border-slate-100 dark:border-[#1E293B]">
            <MessageSquare class="w-4 h-4 text-rose-600 dark:text-rose-400" />
            <span>Remarks & Notes</span>
          </div>
          <p class="text-xs text-slate-700 dark:text-slate-300 leading-relaxed italic bg-slate-50/80 dark:bg-[#0F172A] p-3 rounded-xl border border-slate-100 dark:border-[#1E293B]">
            "{{ teacher.remark }}"
          </p>
        </div>

        <!-- Attendance Analytics Section -->
        <div v-if="teacher?._id" class="pt-2">
          <TeacherAttendanceAnalytics
            :key="teacher?._id"
            :userId="teacher._id"
            :userName="teacher.name"
            :selectedYear="selectedYear"
            :attendanceData="attendanceData"
            @update:selectedYear="handleYearChange"
            @filter-change="handleFilterChange"
          />
        </div>
      </div>

      <!-- No Teacher Data Fallback -->
      <div
        v-else
        class="p-12 text-center"
      >
        <div class="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 mx-auto mb-3">
          <User class="w-7 h-7" />
        </div>
        <p class="text-slate-500 text-sm font-medium">
          No teacher data available for display.
        </p>
      </div>
    </div>
  </div>
  <LeaveBalanceModal
    v-if="showLeaveBalance && teacher"
    v-model="showLeaveBalance"
    :userId="teacher._id"
    :userName="teacher.name"
  />
</template>

<script setup>
  import { defineProps, defineEmits, ref, computed, watch } from "vue";
  import { useStore } from "vuex";
  import LeaveBalanceModal from "@/components/LeaveBalance.vue";
  import TeacherAttendanceAnalytics from "@/components/Teacher/AttendanceGraph.vue";
  import {
    X,
    Pencil,
    Paperclip,
    User,
    Briefcase,
    MapPin,
    GraduationCap,
    MessageSquare,
    Calendar,
    Shield,
    Mail,
    Phone,
    Cake,
    Circle,
    BookOpen,
    CreditCard,
    FileText,
    Home,
    Mailbox,
    Map,
    Building,
    Hash,
  } from "lucide-vue-next";

  const props = defineProps({
    teacher: {
      type: Object,
      default: null,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
  });
  const store = useStore();
  const startDate = ref(null);
  const endDate = ref(null);
  const selectedYear = ref(new Date().getFullYear());

  const attendanceData = computed(() => {
    const userData = store.state.teacherAttendanceYearly?.[props.teacher?._id];
    if (!userData) return {};
    const rangeKey =
      startDate.value && endDate.value
        ? `${selectedYear.value}_${startDate.value}_${endDate.value}`
        : `${selectedYear.value}_full`;
    return userData?.[rangeKey] ?? {};
  });

  const loadAttendance = async () => {
    if (!props.teacher?._id) return;
    await store.dispatch("fetchTeacherYearlyAttendance", {
      userId: props.teacher._id,
      year: selectedYear.value,
      startDate: startDate.value,
      endDate: endDate.value,
    });
  };

  const handleYearChange = (year) => {
    selectedYear.value = year;
    startDate.value = null;
    endDate.value = null;
  };

  const handleFilterChange = async ({ year, startDate: s, endDate: e }) => {
    startDate.value = s;
    endDate.value = e;
    await store.dispatch("fetchTeacherYearlyAttendance", {
      userId: props.teacher._id,
      year,
      startDate: s,
      endDate: e,
    });
  };

  watch(
    () => [props.teacher?._id, selectedYear.value],
    () => {
      loadAttendance();
    },
    { immediate: true },
  );

  const emit = defineEmits(["close"]);
  const showLeaveBalance = ref(false);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateString;
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return "N/A";
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const isImage = (urlOrBase64) => {
    if (!urlOrBase64) return false;
    if (/\.(jpeg|jpg|gif|png|svg|webp)$/i.test(urlOrBase64)) {
      return true;
    }
    if (urlOrBase64.startsWith("data:image/")) {
      return true;
    }
    return false;
  };

  const isPdf = (urlOrBase64) => {
    if (!urlOrBase64) return false;
    if (/\.pdf$/i.test(urlOrBase64)) {
      return true;
    }
    if (urlOrBase64.startsWith("data:application/pdf")) {
      return true;
    }
    return false;
  };

  const getInitials = (name) => {
    if (!name) return "";
    return name
      .trim()
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };
</script>

<style scoped>
  .scale-95 {
    transform: scale(0.97);
  }
  .opacity-0 {
    opacity: 0;
  }
  .scale-100 {
    transform: scale(1);
  }
  .opacity-100 {
    opacity: 1;
  }
</style>
